import React, { useState, useEffect, useCallback } from 'react';

import { Form, SimpleItem } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import ScrollView from 'devextreme-react/scroll-view';
import TextBox from 'devextreme-react/text-box';
import Switch from 'devextreme-react/switch';
import SelectBox from 'devextreme-react/select-box';
import { formatMessage } from 'devextreme/localization';
import notify from 'devextreme/ui/notify';
import 'devextreme-react/text-area';

import GlobalLoader from '../loader/GlobalLoader';

import CustomerSsoApi from '../../api/CustomerSsoApi';

import CustomerSsoConfigurationDto from '../../classes/dtos/accountmodule/CustomerSsoConfigurationDto';
import { UserRoleViewer, UserRoleEditor, UserRoleAdministrator } from '../../classes/consts/UserRole';

import './CustomerSso.css';

interface DomainRow {
    Domain: string;
    IsVerified: boolean;
    RecordName: string;
    RecordValue: string;
    // Added in this screen and not saved yet: the server has not produced its verification record.
    IsNew: boolean;
}

interface IdpForm {
    IsEnabled: boolean;
    AutoProvisionUsers: boolean;
    AutoProvisionRole: number;
    LoginSlug: string;
    IdpMetadataUrl: string;
    IdpEntityId: string;
    IdpSignOnUrl: string;
    IdpSigningCertificate: string;
}

const emptyForm: IdpForm = { IsEnabled: false, AutoProvisionUsers: false, AutoProvisionRole: UserRoleViewer, LoginSlug: '', IdpMetadataUrl: '', IdpEntityId: '', IdpSignOnUrl: '', IdpSigningCertificate: '' };

const domainPattern = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

function normalizeDomain(value: string): string {
    return value.trim().replace(/^@/, '').replace(/\.$/, '').toLowerCase();
}

function showError(message: string): void {
    notify({ message: message, width: 'auto' }, 'error', 6000);
}

function copyToClipboard(text: string): void {
    navigator.clipboard?.writeText(text).then(() => notify({ message: formatMessage('CustomerSso_Copied'), width: 'auto' }, 'success', 1500));
}

const CustomerSso: React.FC = () => {

    const [loaded, setLoaded] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [verifying, setVerifying] = useState<string | null>(null);

    const [configuration, setConfiguration] = useState<CustomerSsoConfigurationDto | null>(null);
    const [form, setForm] = useState<IdpForm>(emptyForm);
    const [domains, setDomains] = useState<DomainRow[]>([]);
    const [newDomain, setNewDomain] = useState<string>('');

    const load = useCallback(async () => {
        const result = await CustomerSsoApi.GetConfigurationAsync();

        if (result.IsFailed()) {
            showError(result.ErrorMessage);
            setLoaded(true);
            return;
        }

        const loadedConfiguration = result.Result;

        setConfiguration(loadedConfiguration);
        setForm(loadedConfiguration === null ? emptyForm : {
            IsEnabled: loadedConfiguration.IsEnabled,
            AutoProvisionUsers: loadedConfiguration.AutoProvisionUsers,
            AutoProvisionRole: loadedConfiguration.AutoProvisionRole,
            LoginSlug: loadedConfiguration.LoginSlug,
            IdpMetadataUrl: loadedConfiguration.IdpMetadataUrl,
            IdpEntityId: loadedConfiguration.IdpEntityId,
            IdpSignOnUrl: loadedConfiguration.IdpSignOnUrl,
            IdpSigningCertificate: loadedConfiguration.IdpSigningCertificate
        });
        setDomains(loadedConfiguration === null ? [] : loadedConfiguration.Domains.map(d => ({
            Domain: d.Domain,
            IsVerified: d.IsVerified,
            RecordName: d.VerificationRecordName,
            RecordValue: d.VerificationRecordValue,
            IsNew: false
        })));
        setLoaded(true);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const addDomain = () => {
        const domain = normalizeDomain(newDomain);

        if (!domainPattern.test(domain)) {
            showError(formatMessage('CustomerSso_InvalidDomain'));
            return;
        }

        if (!domains.some(d => d.Domain === domain)) {
            setDomains([...domains, { Domain: domain, IsVerified: false, RecordName: '', RecordValue: '', IsNew: true }]);
        }

        setNewDomain('');
    };

    const removeDomain = (domain: string) => {
        setDomains(domains.filter(d => d.Domain !== domain));
    };

    const verifyDomain = async (domain: string) => {
        setVerifying(domain);

        const result = await CustomerSsoApi.VerifyDomainAsync(domain);

        setVerifying(null);

        if (result.IsFailed()) {
            showError(result.ErrorMessage);
            return;
        }

        notify({ message: formatMessage('CustomerSso_DomainVerifiedMessage'), width: 'auto' }, 'success', 2000);
        await load();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        const result = await CustomerSsoApi.SaveConfigurationAsync(
            form.IsEnabled,
            form.AutoProvisionUsers,
            form.AutoProvisionRole,
            form.LoginSlug,
            form.IdpEntityId,
            form.IdpSignOnUrl,
            form.IdpMetadataUrl,
            form.IdpSigningCertificate,
            domains.map(d => d.Domain));

        setSaving(false);

        if (result.IsFailed()) {
            showError(result.ErrorMessage);
            // The server may have saved part of the change before failing (e.g. the B2C update): show what it kept.
            await load();
            return;
        }

        notify({ message: formatMessage('CustomerSso_Saved'), width: 'auto' }, 'success', 2000);
        await load();
    };

    if (!loaded) {
        return <GlobalLoader />;
    }

    const statusKey = configuration === null ? null : 'CustomerSso_Status_' + configuration.ProvisioningStatus;

    return (
        <div className={"block-card block-card--fill customer-sso"}>
            <div className={"block-card__header"}>
                <h2>{formatMessage('CustomerSso_Title')}</h2>
            </div>
            <p className="customer-sso-intro">{formatMessage('CustomerSso_Intro')}</p>

            <form className="customer-sso-form" onSubmit={handleSubmit}>
                <ScrollView className="customer-sso-body" showScrollbar="onHover" useNative={false} width="100%">
                <section className="customer-sso-section">
                    <h3>{formatMessage('CustomerSso_LoginUrl')}</h3>
                    <p>{formatMessage('CustomerSso_LoginUrlHelp')}</p>
                    <div className="customer-sso-copy-row">
                        <TextBox
                            value={form.LoginSlug}
                            onValueChange={(value) => setForm(previous => ({ ...previous, LoginSlug: value.trim().toLowerCase() }))}
                            valueChangeEvent="input"
                            label={formatMessage('CustomerSso_LoginSlug')}
                            labelMode="static"
                            placeholder={configuration === null ? '' : undefined}
                            width="100%" />
                        {form.LoginSlug !== '' && (
                            <Button icon="copy" text={formatMessage('CustomerSso_Copy')} onClick={() => copyToClipboard(window.location.origin + '/sso/' + form.LoginSlug)} />
                        )}
                    </div>
                    {form.LoginSlug !== '' && <code>{window.location.origin + '/sso/' + form.LoginSlug}</code>}
                </section>

                <section className="customer-sso-section">
                    <h3>{formatMessage('CustomerSso_ServiceProvider')}</h3>
                    {configuration === null || configuration.ServiceProviderMetadataUrl === '' ? (
                        <p>{formatMessage('CustomerSso_ServiceProviderAfterSave')}</p>
                    ) : (
                        <div className="customer-sso-copy-row">
                            <TextBox value={configuration.ServiceProviderMetadataUrl} readOnly={true} label={formatMessage('CustomerSso_ServiceProviderMetadataUrl')} labelMode="static" width="100%" />
                            <Button icon="copy" text={formatMessage('CustomerSso_Copy')} onClick={() => copyToClipboard(configuration.ServiceProviderMetadataUrl)} />
                        </div>
                    )}
                </section>

                <section className="customer-sso-section">
                    <h3>{formatMessage('CustomerSso_IdentityProvider')}</h3>
                    <p>{formatMessage('CustomerSso_IdpHelp')}</p>
                    <Form
                        colCount={1}
                        formData={form}
                        showColonAfterLabel={true}
                        onFieldDataChanged={(e) => setForm(previous => ({ ...previous, [e.dataField as string]: e.value }))}>
                        <SimpleItem dataField="IdpMetadataUrl" editorType="dxTextBox" label={{ text: formatMessage('CustomerSso_IdpMetadataUrl') }} />
                        <SimpleItem dataField="IdpEntityId" editorType="dxTextBox" label={{ text: formatMessage('CustomerSso_IdpEntityId') }} />
                        <SimpleItem dataField="IdpSignOnUrl" editorType="dxTextBox" label={{ text: formatMessage('CustomerSso_IdpSignOnUrl') }} />
                        <SimpleItem dataField="IdpSigningCertificate" editorType="dxTextArea" editorOptions={{ height: 120, spellcheck: false }} label={{ text: formatMessage('CustomerSso_IdpSigningCertificate') }} />
                    </Form>
                </section>

                <section className="customer-sso-section">
                    <h3>{formatMessage('CustomerSso_Domains')}</h3>
                    <p>{formatMessage('CustomerSso_DomainsHelp')}</p>

                    {domains.length > 0 && (
                        <table className="customer-sso-domains">
                            <thead>
                                <tr>
                                    <th>{formatMessage('CustomerSso_Domain')}</th>
                                    <th>{formatMessage('CustomerSso_DomainStatus')}</th>
                                    <th>{formatMessage('CustomerSso_DnsRecord')}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {domains.map(d => (
                                    <tr key={d.Domain}>
                                        <td>{d.Domain}</td>
                                        <td>{d.IsVerified ? formatMessage('CustomerSso_DomainVerified') : formatMessage('CustomerSso_DomainPending')}</td>
                                        <td>
                                            {d.IsNew ? formatMessage('CustomerSso_DomainUnsaved') : d.IsVerified ? '' : (
                                                <div className="customer-sso-record">
                                                    <div><strong>{d.RecordName}</strong></div>
                                                    <div className="customer-sso-record-value">
                                                        <code>{d.RecordValue}</code>
                                                        <Button icon="copy" stylingMode="text" hint={formatMessage('CustomerSso_Copy')} onClick={() => copyToClipboard(d.RecordValue)} />
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                        <td className="customer-sso-actions">
                                            {!d.IsNew && !d.IsVerified && (
                                                <Button text={formatMessage('CustomerSso_Verify')} disabled={verifying !== null} onClick={() => verifyDomain(d.Domain)} />
                                            )}
                                            <Button text={formatMessage('CustomerSso_Remove')} stylingMode="text" onClick={() => removeDomain(d.Domain)} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <div className="customer-sso-copy-row">
                        <TextBox
                            value={newDomain}
                            onValueChange={setNewDomain}
                            valueChangeEvent="input"
                            placeholder={formatMessage('CustomerSso_DomainPlaceholder')}
                            onEnterKey={addDomain}
                            width="100%" />
                        <Button text={formatMessage('CustomerSso_AddDomain')} onClick={addDomain} />
                    </div>
                </section>

                <section className="customer-sso-section">
                    <h3>{formatMessage('CustomerSso_AutoProvision')}</h3>
                    <p>{formatMessage('CustomerSso_AutoProvisionHelp')}</p>
                    <div className="customer-sso-enable">
                        <Switch value={form.AutoProvisionUsers} onValueChange={(value) => setForm(previous => ({ ...previous, AutoProvisionUsers: value }))} />
                        <span>{formatMessage('CustomerSso_AutoProvisionEnabled')}</span>
                    </div>
                    {form.AutoProvisionUsers && (
                        <SelectBox
                            items={[
                                { Id: UserRoleViewer, Text: formatMessage('CustomerSso_RoleViewer') },
                                { Id: UserRoleEditor, Text: formatMessage('CustomerSso_RoleEditor') },
                                { Id: UserRoleAdministrator, Text: formatMessage('CustomerSso_RoleAdministrator') }
                            ]}
                            displayExpr="Text"
                            valueExpr="Id"
                            value={form.AutoProvisionRole}
                            onValueChange={(value) => setForm(previous => ({ ...previous, AutoProvisionRole: value }))}
                            label={formatMessage('CustomerSso_AutoProvisionRole')}
                            labelMode="static"
                            width="100%" />
                    )}
                </section>

                <section className="customer-sso-section customer-sso-enable">
                    <Switch value={form.IsEnabled} onValueChange={(value) => setForm(previous => ({ ...previous, IsEnabled: value }))} />
                    <span>{formatMessage('CustomerSso_Enabled')}</span>
                    {statusKey !== null && configuration !== null && (
                        <span className="customer-sso-status">
                            {formatMessage('CustomerSso_Status')} : {formatMessage(statusKey) || configuration.ProvisioningStatus}
                        </span>
                    )}
                </section>

                {configuration !== null && configuration.ProvisioningError !== '' && (
                    <div className="customer-sso-error" role="alert">{configuration.ProvisioningError}</div>
                )}

                </ScrollView>

                <div className="customer-sso-buttons">
                    <Button text={formatMessage('CustomerSso_Save')} type="default" useSubmitBehavior={true} width={120} disabled={saving} />
                </div>
            </form>
        </div>
    );
};

export default CustomerSso;
